/* eslint-disable react-refresh/only-export-components */
import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Collapse,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  LuTriangleAlert,
  LuRefreshCw,
  LuHouse,
  LuChevronDown,
  LuCopy,
  LuCheck,
} from 'react-icons/lu';
import AppIcon from '../icons/app-icon';

export type ErrorFallbackVariant = 'page' | 'section';

export interface ErrorFallbackProps {
  /** The thrown value (anything — coerced to an Error for display). */
  error?: unknown;
  /** `page` fills the viewport (catastrophic); `section` sits inside the layout. */
  variant?: ErrorFallbackVariant;
  onRetry?: () => void;
  onGoHome?: () => void;
  /** Override the primary (retry) button label. */
  retryLabel?: string;
}

/**
 * Shared console logger for every app error boundary. A single hook point so a
 * real logging service can be wired in later without touching each boundary.
 */
export const logBoundaryError = (
  error: Error,
  info: { componentStack?: string | null }
) => {
  console.error(
    '[ErrorBoundary] Uncaught render error:',
    error,
    info?.componentStack
  );
};

const toError = (error: unknown): Error | undefined => {
  if (error instanceof Error) return error;
  if (typeof error === 'string' && error.trim()) return new Error(error);
  return undefined;
};

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/**
 * The friendly, theme-aware fallback shown when a render error is caught. It
 * surfaces the actual error to the user (message always visible, full stack
 * available on demand with one-click copy) and offers a clear way to recover.
 */
const ErrorFallback = ({
  error,
  variant = 'section',
  onRetry,
  onGoHome,
  retryLabel,
}: ErrorFallbackProps) => {
  const theme = useTheme();
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  const err = toError(error);
  const message = err?.message?.trim() || 'An unexpected error occurred.';
  const stack = err?.stack?.trim();
  const isPage = variant === 'page';

  const handleCopy = () => {
    const payload = [message, stack].filter(Boolean).join('\n\n');
    try {
      navigator.clipboard?.writeText(payload);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — nothing to do */
    }
  };

  return (
    <Box
      role='alert'
      aria-live='assertive'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 2.5,
        width: '100%',
        maxWidth: 540,
        mx: 'auto',
        px: 3,
        py: isPage ? 6 : 5,
        minHeight: isPage ? '100vh' : '55vh',
        backgroundColor: isPage ? 'background.default' : 'transparent',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: 76,
          height: 76,
          borderRadius: '50%',
          backgroundColor: alpha(theme.palette.warning.main, 0.12),
        }}
      >
        <AppIcon icon={LuTriangleAlert} size={36} color='warning.main' />
      </Box>

      <Box>
        <Typography variant='h5' sx={{ fontWeight: 700, color: 'text.primary' }}>
          {isPage ? 'Something went wrong' : 'This page hit a snag'}
        </Typography>
        <Typography variant='body2' sx={{ mt: 0.5, color: 'text.secondary' }}>
          {isPage ? 'حدث خطأ ما' : 'حدث خطأ في هذه الصفحة'}
        </Typography>
        <Typography
          variant='body2'
          sx={{ mt: 1.5, color: 'text.secondary', maxWidth: 440, mx: 'auto' }}
        >
          {isPage
            ? 'The app ran into an unexpected error. You can reload the page or head back home.'
            : "Don't worry — the rest of the app is still running. Try again, or head back and keep going."}
        </Typography>
      </Box>

      <Box
        sx={{
          width: '100%',
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.error.main, 0.28)}`,
          backgroundColor: alpha(theme.palette.error.main, 0.07),
          px: 2,
          py: 1.5,
          textAlign: 'left',
        }}
      >
        <Typography
          variant='caption'
          sx={{
            display: 'block',
            mb: 0.5,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            color: 'error.main',
          }}
        >
          Error
        </Typography>
        <Typography
          sx={{
            color: 'text.primary',
            fontFamily: MONO,
            fontSize: 13,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message}
        </Typography>
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        justifyContent='center'
        sx={{ width: '100%' }}
      >
        {onRetry && (
          <Button
            variant='contained'
            onClick={onRetry}
            startIcon={<AppIcon icon={LuRefreshCw} fontSize='small' />}
            sx={{ minWidth: 150 }}
          >
            {retryLabel ?? (isPage ? 'Reload' : 'Try again')}
          </Button>
        )}
        {onGoHome && (
          <Button
            variant='outlined'
            color='inherit'
            onClick={onGoHome}
            startIcon={<AppIcon icon={LuHouse} fontSize='small' />}
            sx={{ minWidth: 150 }}
          >
            Back to home
          </Button>
        )}
      </Stack>

      {stack && (
        <Box sx={{ width: '100%' }}>
          <Button
            variant='text'
            color='inherit'
            size='small'
            onClick={() => setShowDetails((prev) => !prev)}
            endIcon={
              <AppIcon
                icon={LuChevronDown}
                fontSize='small'
                sx={{
                  transform: showDetails ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease',
                }}
              />
            }
            sx={{ color: 'text.secondary' }}
          >
            {showDetails ? 'Hide technical details' : 'Show technical details'}
          </Button>
          <Collapse in={showDetails} unmountOnExit>
            <Box
              sx={{
                position: 'relative',
                mt: 1,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: alpha(theme.palette.text.primary, 0.04),
                textAlign: 'left',
              }}
            >
              <Tooltip title={copied ? 'Copied' : 'Copy details'}>
                <IconButton
                  size='small'
                  onClick={handleCopy}
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    color: copied ? 'success.main' : 'text.secondary',
                  }}
                >
                  <AppIcon icon={copied ? LuCheck : LuCopy} fontSize='small' />
                </IconButton>
              </Tooltip>
              <Box
                component='pre'
                sx={{
                  m: 0,
                  p: 2,
                  pr: 5,
                  maxHeight: 220,
                  overflow: 'auto',
                  fontFamily: MONO,
                  fontSize: 12,
                  lineHeight: 1.6,
                  color: 'text.secondary',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {stack}
              </Box>
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default ErrorFallback;
